import React, { useState, useRef, useEffect } from 'react';
import { RefreshCw, ChevronDown, Check } from 'lucide-react';
import { Button } from './ui/button';

export default function PageHeader({ title, subtitle, actions, onRefresh, badge }) {
  return (
    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-6">
      <div>
        <div className="flex items-center gap-3">
          <h1 className="font-display text-2xl md:text-3xl font-bold text-white">{title}</h1>
          {badge}
        </div>
        {subtitle && <p className="text-slate-400 text-sm mt-1">{subtitle}</p>}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {onRefresh && (
          <Button variant="outline" size="icon" className="h-9 w-9 border-white/10 bg-white/5 text-slate-300 hover:bg-white/10" onClick={onRefresh}>
            <RefreshCw size={14} />
          </Button>
        )}
        {actions}
      </div>
    </div>
  );
}

function AccountAvatar({ a, size = 'sm' }) {
  const cls = size === 'lg' ? 'w-7 h-7' : 'w-6 h-6';
  if (a?.profile_photo_url) {
    const src = a.profile_photo_url.startsWith('http') ? a.profile_photo_url : `${process.env.REACT_APP_BACKEND_URL}${a.profile_photo_url}`;
    return <img src={src} alt={a.name} className={`${cls} rounded-full object-cover shrink-0`} />;
  }
  return (
    <div className={`${cls} rounded-full bg-gradient-to-br ${a?.color || 'from-indigo-500 to-violet-500'} flex items-center justify-center text-[10px] text-white font-bold shrink-0`}>
      {a?.initial || a?.name?.[0] || '?'}
    </div>
  );
}

export function AccountSelector({ value, onChange, accounts, allowAll = false }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const current = value === 'all' ? null : accounts.find(a => a.id === value);
  return (
    <div className="relative" ref={ref}>
      <button data-testid="account-selector-button" onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2 px-2.5 py-1.5 rounded-xl border border-white/10 bg-white/[0.04] hover:bg-white/10 text-white text-sm min-w-[160px] justify-between transition-colors h-9">
        <span className="flex items-center gap-2 truncate">
          {current ? <><AccountAvatar a={current} /> <span className="truncate">{current.name}</span></> : <span className="text-slate-300">All accounts</span>}
        </span>
        <ChevronDown size={14} className={`text-slate-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-64 rounded-2xl border border-white/10 bg-[#0B0B0E] shadow-2xl z-50 overflow-hidden" data-testid="account-selector-dropdown">
          <div className="max-h-80 overflow-y-auto scrollbar-thin py-1">
            {allowAll && (
              <button data-testid="account-selector-all" onClick={() => { onChange('all'); setOpen(false); }}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-sm transition-colors ${value === 'all' ? 'bg-sky-500/15 text-white' : 'text-slate-300 hover:bg-white/5'}`}>
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-slate-600 to-slate-700 flex items-center justify-center text-[10px] text-white font-bold shrink-0">★</div>
                <span className="flex-1 text-left">All accounts</span>
                {value === 'all' && <Check size={14} className="text-sky-400" />}
              </button>
            )}
            {accounts.map(a => {
              const active = value === a.id;
              return (
                <button key={a.id} data-testid={`account-selector-${a.id}`} onClick={() => { onChange(a.id); setOpen(false); }}
                  className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-sm transition-colors ${active ? 'bg-sky-500/15 text-white' : 'text-slate-300 hover:bg-white/5'}`}>
                  <div className="relative shrink-0">
                    <AccountAvatar a={a} size="lg" />
                    {a.status === 'ready' && <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-sky-500 rounded-full border-2 border-[#0B0B0E]" />}
                  </div>
                  <span className="flex-1 text-left truncate">{a.name}</span>
                  {active && <Check size={14} className="text-sky-400" />}
                </button>
              );
            })}
            {accounts.length === 0 && <div className="text-center text-[11px] text-slate-500 py-6 px-3">No connected account</div>}
          </div>
        </div>
      )}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, desc, action }) {
  return (
    <div className="text-center py-16 px-6">
      {Icon && <Icon size={36} className="mx-auto text-slate-600 mb-4" />}
      <div className="text-white font-semibold text-base mb-1">{title}</div>
      {desc && <div className="text-slate-500 text-sm mb-4 max-w-md mx-auto">{desc}</div>}
      {action}
    </div>
  );
}
